import { Component, Input, OnInit } from '@angular/core';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { forkJoin, from, of, throwError } from 'rxjs';
import { catchError, concatMap, tap, toArray } from 'rxjs/operators';
import { IApiAddSyncSageContactInput, IApiBranch, IApiContact, IApiInvestigation, IApiSageContact } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { ContactService } from 'src/app/shared/services';

/** A Bill To contact that has to be linked to a SAGE contact */
export interface ISageContactTarget {
  contact: IApiContact;
  /** Branch the contact bills through, decides the company name and address sent to SAGE */
  branch?: IApiBranch;
  label: string;
}

/** What the dialog hands back: the contacts that were linked, and whether every one of them was */
export interface ISageContactSyncResult {
  complete: boolean;
  contacts: IApiContact[];
}

interface ISageContactRow extends ISageContactTarget {
  sageContacts: IApiSageContact[];
  selectedSageId: string;
  status: 'pending' | 'saving' | 'done' | 'error';
  error?: string;
  loadError?: string;
  result?: IApiContact;
}

@Component({
  selector: 'app-sage-contact-selection-modal',
  templateUrl: './sage-contact-selection-modal.component.html',
  styleUrls: ['./sage-contact-selection-modal.component.scss']
})
export class SageContactSelectionModalComponent extends DialogContentBase implements OnInit {
  @Input() investigation: IApiInvestigation;
  @Input() targets: ISageContactTarget[] = [];

  public rows: ISageContactRow[] = [];
  public isSaving = false;

  constructor(
    public dialogRef: DialogRef,
    private contactService: ContactService,
    private loader: LoaderService,
    private notification: NotificationsService
  ) {
    super(dialogRef);
  }

  /** Every contact needs a choice, unless it was already linked */
  public get canSave(): boolean {
    return !this.isSaving && this.rows.some(({ status }) => status !== 'done')
      && this.rows.every(({ status, selectedSageId }) => status === 'done' || !!selectedSageId);
  }

  ngOnInit(): void {
    this.rows = this.targets.map((target) => ({ ...target, sageContacts: [], selectedSageId: '', status: 'pending' }));

    this.loader.show$(forkJoin(this.rows.map((row) =>
      this.contactService.getSageContactsByName(row.contact.id).pipe(
        // a failed search still leaves "create new" as a choice
        catchError((error) => {
          row.loadError = error?.message || 'The SAGE contacts could not be loaded.';
          return of([]);
        })
      )
    ))).subscribe((lists) => lists.forEach((sageContacts, index) => this.rows[index].sageContacts = sageContacts));
  }

  /** Hands back whatever was linked, even when the dialog is closed half way, so the caller can pick up from there */
  public close() {
    const contacts = this.rows.filter(({ status, result }) => status === 'done' && result).map(({ result }) => result);
    this.dialogRef.close({
      complete: contacts.length === this.rows.length,
      contacts
    } as ISageContactSyncResult);
  }

  public submit() {
    this.isSaving = true;

    // one at a time: SAGE customer ids are handed out from the latest id, so parallel creates could collide
    from(this.rows.filter(({ status }) => status !== 'done')).pipe(
      concatMap((row) => this.sync(row)),
      toArray()
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.close();
      },
      // the failed row shows its message and the ones after it stay pending, saving again carries on from there
      error: () => this.isSaving = false
    });
  }

  private sync(row: ISageContactRow) {
    row.status = 'saving';
    row.error = null;

    const input: IApiAddSyncSageContactInput = {
      BillToId: row.contact.id,
      BillToBranchId: row.branch?.id || null,
      InvestigationId: this.investigation?.id,
      SageId: row.selectedSageId,
      SageKey: row.sageContacts.find(({ id }) => id === row.selectedSageId)?.key || '',
      createNew: row.selectedSageId === 'NEW',
    };

    return this.contactService.addSyncSageContact(input).pipe(
      tap((response) => {
        if (!response?.sageId) throw new Error('SAGE did not link this contact. Please try again.');
        row.status = 'done';
        row.result = response;
      }),
      catchError((error) => {
        row.status = 'error';
        row.error = error?.message || 'The contact could not be synced with SAGE.';
        return throwError(error);
      })
    );
  }
}
